import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Chip, Switch, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup, CircularProgress, Alert,
} from '@mui/material';
import { People as PeopleIcon, FileDownload as DownloadIcon, Print as PrintIcon, ToggleOn as OnIcon, ToggleOff as OffIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { ratingAPI } from '../../services/api';
import { errorText } from '../../utils/format';
import { printPage, esc } from '../../utils/print';
import { ScoreView } from './shared';

const STATUS = {
  COMPLETE: { label: 'Completed', bg: '#c8e6c9', fg: '#1b5e20' },
  PARTIAL: { label: 'Partly done', bg: '#ffe0b2', fg: '#e65100' },
  NONE: { label: 'Not started', bg: '#ffcdd2', fg: '#b71c1c' },
};

export default function ParticipationTab({ config, settings, participation, reload, notify }) {
  const scaleType = config?.scaleType || 'STARS_5';
  const [classId, setClassId] = useState('');
  const [students, setStudents] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [loading, setLoading] = useState(false);

  const partBySection = new Map(participation.map((p) => [p.sectionId, p]));
  const classes = [...new Map(settings.map((s) => [s.classId, s.className])).entries()].map(([id, name]) => ({ id, name }));
  const rows = settings.filter((s) => !classId || String(s.classId) === String(classId))
    .sort((a, b) => `${a.className}${a.sectionName}`.localeCompare(`${b.className}${b.sectionName}`));

  const toggle = async (sectionId, enabled) => {
    try { await ratingAPI.toggleSetting(sectionId, enabled); notify(`Feedback ${enabled ? 'opened' : 'closed'} for the batch`); reload(); }
    catch (e) { notify(errorText(e, 'Could not update'), 'error'); }
  };

  const bulk = async (enabled) => {
    try { const r = await ratingAPI.bulkSettings({ classId: classId || undefined, enabled }); notify(r.data.message); reload(); }
    catch (e) { notify(errorText(e, 'Could not update'), 'error'); }
  };

  const openStudents = async (row) => {
    setStudents({ className: row.className, sectionName: row.sectionName, list: null });
    setFilter('ALL');
    setLoading(true);
    try {
      const r = (await ratingAPI.getParticipationStudents(row.sectionId)).data;
      setStudents({ className: r.className, sectionName: r.sectionName, list: r.students });
    } catch (e) { notify(errorText(e, 'Could not load students'), 'error'); setStudents(null); }
    setLoading(false);
  };

  const shownStudents = (students?.list || []).filter((s) => filter === 'ALL' || s.status === filter);

  const exportStudents = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(shownStudents.map((s) => ({
      'Admission no': s.admissionNo, Student: s.name, 'Feedback given': s.rated, 'Faculty to rate': s.expected, Status: STATUS[s.status].label,
    }))), 'Feedback status');
    XLSX.writeFile(wb, `feedback_status_${students.className}_${students.sectionName}.xlsx`.replace(/\s+/g, '_'));
  };

  const printStudents = () => {
    const body = `<table><thead><tr><th>Admission no</th><th>Student</th><th class="c">Given</th><th class="c">To give</th><th>Status</th></tr></thead><tbody>
      ${shownStudents.map((s) => `<tr><td>${esc(s.admissionNo)}</td><td>${esc(s.name)}</td><td class="c">${s.rated}</td><td class="c">${s.expected}</td><td>${STATUS[s.status].label}</td></tr>`).join('')}</tbody></table>`;
    printPage('Faculty Feedback Status', `${students.className} - ${students.sectionName}${filter !== 'ALL' ? ' (' + STATUS[filter].label + ')' : ''}`, body);
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Turn feedback on for a batch to let its students rate their faculty from the Student app. See how many students in each class have taken part and follow up with those who have not.
      </Alert>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField select size="small" label="Program / year" value={classId} onChange={(e) => setClassId(e.target.value)} sx={{ minWidth: 260 }}>
          <MenuItem value="">All programs</MenuItem>
          {classes.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="success" size="small" startIcon={<OnIcon />} onClick={() => bulk(true)}>Open for {classId ? 'this program' : 'all batches'}</Button>
          <Button variant="outlined" color="inherit" size="small" startIcon={<OffIcon />} onClick={() => bulk(false)}>Close for {classId ? 'this program' : 'all batches'}</Button>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead><TableRow>
              <TableCell>Program / year</TableCell><TableCell>Batch</TableCell><TableCell align="center">Feedback open</TableCell><TableCell align="center">Students</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Students who took part</TableCell><TableCell sx={{ minWidth: 160 }}>Forms completed</TableCell><TableCell>Average</TableCell><TableCell align="right">Students</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.map((s) => {
                const p = partBySection.get(s.sectionId);
                const part = p ? Number(p.participationPct) : 0;
                const comp = p ? Number(p.completionPct) : 0;
                return (
                  <TableRow key={s.sectionId} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{s.className}</TableCell>
                    <TableCell>{s.sectionName}</TableCell>
                    <TableCell align="center"><Switch checked={s.enabled} onChange={(e) => toggle(s.sectionId, e.target.checked)} color="success" /></TableCell>
                    <TableCell align="center">{p ? p.students : 0}</TableCell>
                    <TableCell>
                      {p && p.students > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={Math.min(100, part)} sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: '#eceff1', '& .MuiLinearProgress-bar': { bgcolor: part >= 75 ? '#2e7d32' : part >= 40 ? '#f9a825' : '#d32f2f' } }} />
                          <Typography variant="caption" fontWeight={700} sx={{ minWidth: 70 }}>{p.participated} of {p.students} ({part.toFixed(0)}%)</Typography>
                        </Box>
                      ) : <Typography variant="caption" color="text.secondary">No students in this batch</Typography>}
                    </TableCell>
                    <TableCell>{p && p.expectedRatings > 0 ? <Typography variant="caption">{p.ratingsCount} of {p.expectedRatings} ({comp.toFixed(0)}%)</Typography> : '-'}</TableCell>
                    <TableCell>{p && p.ratingsCount > 0 ? <ScoreView avg10={p.avg10} scaleType={scaleType} size={14} /> : '-'}</TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<PeopleIcon />} onClick={() => openStudents(s)} disabled={!p || p.students === 0}>View</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>No batches found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!students} onClose={() => setStudents(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{students?.className} · {students?.sectionName}</DialogTitle>
        <DialogContent dividers>
          {loading || !students?.list ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box> : (
            <>
              <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v && setFilter(v)} sx={{ mb: 2 }}>
                <ToggleButton value="ALL">All ({students.list.length})</ToggleButton>
                <ToggleButton value="NONE">Not started ({students.list.filter((s) => s.status === 'NONE').length})</ToggleButton>
                <ToggleButton value="PARTIAL">Partly ({students.list.filter((s) => s.status === 'PARTIAL').length})</ToggleButton>
                <ToggleButton value="COMPLETE">Done ({students.list.filter((s) => s.status === 'COMPLETE').length})</ToggleButton>
              </ToggleButtonGroup>
              <Table size="small">
                <TableHead><TableRow><TableCell>Admission no</TableCell><TableCell>Student</TableCell><TableCell align="center">Given / to give</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                <TableBody>
                  {shownStudents.map((s) => (
                    <TableRow key={s.studentId}>
                      <TableCell>{s.admissionNo}</TableCell><TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                      <TableCell align="center">{s.rated} / {s.expected}</TableCell>
                      <TableCell><Chip size="small" label={STATUS[s.status].label} sx={{ bgcolor: STATUS[s.status].bg, color: STATUS[s.status].fg, fontWeight: 700 }} /></TableCell>
                    </TableRow>
                  ))}
                  {shownStudents.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No students</TableCell></TableRow>}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<DownloadIcon />} onClick={exportStudents} disabled={!shownStudents.length}>Excel</Button>
          <Button startIcon={<PrintIcon />} onClick={printStudents} disabled={!shownStudents.length}>Print</Button>
          <Button onClick={() => setStudents(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
